import React, { useState, useRef, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { BookOpen } from 'lucide-react';
import { useLanguage } from '../contexts/LanguageContext';

const LanguageChoiceModal = () => {
  const { setLanguage } = useLanguage();
  const location = useLocation();
  const isWiki =
    location.pathname === '/wiki' || location.pathname.startsWith('/wiki/');
  const [show, setShow] = useState(true);
  const dialogRef = useRef(null);
  const firstButtonRef = useRef(null);

  const handleChoice = (lang) => {
    setLanguage(lang);
    setShow(false);
  };

  const handleDismiss = () => {
    setShow(false);
  };

  useEffect(() => {
    if (!show) return;
    firstButtonRef.current?.focus({ preventScroll: true });
  }, [show]);

  useEffect(() => {
    if (!show) return;
    const onKeyDown = (e) => {
      if (e.key === 'Escape') {
        e.preventDefault();
        handleDismiss();
      }
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [show]);

  if (!show) return null;

  if (isWiki) {
    return (
      <div
        ref={dialogRef}
        className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-black/40"
        aria-modal="true"
        role="dialog"
        aria-labelledby="language-modal-title"
        aria-describedby="language-modal-desc"
      >
        <div
          className="relative w-full max-w-sm"
          role="document"
          onKeyDown={(e) => e.key === 'Escape' && e.stopPropagation()}
        >
          <div className="rounded-lg border border-gray-200 bg-white p-6 shadow-xl">
            <div className="flex justify-center mb-4">
              <BookOpen className="h-10 w-10 text-blue-600" aria-hidden="true" />
            </div>
            <h2
              id="language-modal-title"
              className="text-center text-gray-900 font-semibold mb-1 text-[17px] tracking-tight"
            >
              Choose language / 选择语言
            </h2>
            <p id="language-modal-desc" className="text-center text-gray-500 text-sm mb-6">
              Select your preferred language
            </p>
            <div className="flex flex-col gap-3">
              <button
                ref={firstButtonRef}
                type="button"
                onClick={() => handleChoice('en')}
                className="w-full min-h-[48px] py-3.5 px-5 rounded-md border border-gray-200 bg-gray-50 text-gray-900 font-medium hover:bg-gray-100 hover:border-gray-300 active:scale-[0.98] transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
              >
                <span className="block">English</span>
                <span className="block text-xs text-gray-500 font-normal mt-0.5">英文</span>
              </button>
              <button
                type="button"
                onClick={() => handleChoice('zh')}
                className="w-full min-h-[48px] py-3.5 px-5 rounded-md border border-gray-200 bg-gray-50 text-gray-900 font-medium hover:bg-gray-100 hover:border-gray-300 active:scale-[0.98] transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
              >
                <span className="block">中文</span>
                <span className="block text-xs text-gray-500 font-normal mt-0.5">Chinese</span>
              </button>
            </div>
            <p className="text-center text-gray-500 text-xs mt-4">
              Press{' '}
              <kbd className="px-1.5 py-0.5 rounded border border-gray-200 bg-white text-gray-600">
                Esc
              </kbd>{' '}
              to close
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div
      ref={dialogRef}
      className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-black/70 sm:backdrop-blur-sm"
      aria-modal="true"
      role="dialog"
      aria-labelledby="language-modal-title"
      aria-describedby="language-modal-desc"
    >
      <div
        className="relative w-full max-w-sm animate-scale-in"
        role="document"
        onKeyDown={(e) => e.key === 'Escape' && e.stopPropagation()}
      >
        <div className="relative overflow-hidden border border-accent/30 rounded-2xl bg-dark-card p-6 shadow-[0_0_40px_-8px_rgba(71,150,227,0.25)]">
          <div className="absolute inset-0 bg-gradient-to-b from-accent/10 via-transparent to-transparent pointer-events-none" />
          <div className="relative z-10 flex justify-center mb-3">
            <span className="text-4xl select-none" role="img" aria-hidden="true">
              🗣️
            </span>
          </div>
          <h2
            id="language-modal-title"
            className="relative z-10 text-center text-white font-semibold mb-1 text-[17px] tracking-tight"
          >
            Choose language / 选择语言
          </h2>
          <p id="language-modal-desc" className="relative z-10 text-center text-gray-400 text-sm mb-6">
            Select your preferred language
          </p>
          <div className="relative z-10 flex flex-col gap-3">
            <button
              ref={firstButtonRef}
              type="button"
              onClick={() => handleChoice('en')}
              className="w-full min-h-[48px] py-3.5 px-5 rounded-xl border border-dark-border bg-dark-surface text-white font-medium hover:bg-dark-elevated hover:border-dark-border-light active:scale-[0.98] transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-accent focus:ring-offset-2 focus:ring-offset-dark-card"
            >
              <span className="block">English</span>
              <span className="block text-xs text-gray-500 font-normal mt-0.5">英文</span>
            </button>
            <button
              type="button"
              onClick={() => handleChoice('zh')}
              className="w-full min-h-[48px] py-3.5 px-5 rounded-xl border border-dark-border bg-dark-surface text-white font-medium hover:bg-dark-elevated hover:border-dark-border-light active:scale-[0.98] transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-accent focus:ring-offset-2 focus:ring-offset-dark-card"
            >
              <span className="block">中文</span>
              <span className="block text-xs text-gray-500 font-normal mt-0.5">Chinese</span>
            </button>
          </div>
          <p className="relative z-10 text-center text-gray-500 text-xs mt-4">
            Press{' '}
            <kbd className="px-1.5 py-0.5 rounded bg-dark-surface border border-dark-border text-gray-400">
              Esc
            </kbd>{' '}
            to close
          </p>
        </div>
      </div>
    </div>
  );
};

export default LanguageChoiceModal;
